import React, { useRef } from 'react';
import { COLORS, TYPE, WEIGHT, LETTER, SPACE } from '../styles/tokens';
import GlassCard from './ui/GlassCard';
import IconBadge from './ui/IconBadge';
import SectionHeading from './ui/SectionHeading';
import useScrollReveal from '../hooks/useScrollReveal';
import { useT } from '../i18n/I18nContext';
import { useTrackSection } from '../hooks/useTrackSection';

const ICONS: React.ReactNode[] = [
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="3" y1="6" x2="21" y2="6" stroke="white" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M16 10a4 4 0 01-8 0" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 14l1.5 1.5L15 11" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="2.5" fill="white" opacity="0.9" />
    <circle cx="5.5" cy="6" r="2" fill="white" opacity="0.7" />
    <circle cx="18.5" cy="6" r="2" fill="white" opacity="0.7" />
    <circle cx="12" cy="20" r="2" fill="white" opacity="0.7" />
    <line x1="12" y1="9.5" x2="6.8" y2="7.5" stroke="white" strokeWidth="1.2" opacity="0.65" />
    <line x1="12" y1="9.5" x2="17.2" y2="7.5" stroke="white" strokeWidth="1.2" opacity="0.65" />
    <line x1="12" y1="14.5" x2="12" y2="18" stroke="white" strokeWidth="1.2" opacity="0.65" />
  </svg>,
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="11" width="18" height="11" rx="2" stroke="white" strokeWidth="1.7" />
    <path d="M7 11V7a5 5 0 0110 0v4" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="16" r="1.5" fill="white" opacity="0.8" />
  </svg>,
];
const TINTS = [COLORS.tintPink, COLORS.tintPurple, COLORS.tintBlue, COLORS.tintMint];
const GLOWS = [COLORS.glowPink, COLORS.glowPurple, COLORS.glowBlue, COLORS.glowMint];

export default function TrustSection() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);
  const headRef  = useScrollReveal<HTMLDivElement>({ fromY: 26, duration: 0.7, start: 'top 80%' });
  const cardsRef = useScrollReveal<HTMLDivElement>({
    fromY: 22, fromScale: 0.97, duration: 0.6, stagger: 0.1, start: 'top 83%',
    selector: '.trust-card',
  });
  useTrackSection('trust', sectionRef);

  return (
    <section
      ref={sectionRef}
      id="trust"
      style={{
        position: 'relative',
        padding: `${SPACE.sectionY} ${SPACE.sectionX}`,
        zIndex: 1,
        background: 'transparent',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div ref={headRef} style={{ marginBottom: 'clamp(32px, 4vw, 52px)', opacity: 0 }}>
          <SectionHeading title={t.trust.titleA} secondLine={t.trust.titleB} />
        </div>

        <div
          ref={cardsRef}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 'clamp(12px, 1.8vw, 18px)',
          }}
          className="trust-grid"
        >
          {t.trust.cards.map((c, i) => (
            <GlassCard
              key={i}
              hoverGlow={GLOWS[i]}
              className="trust-card"
              padding="clamp(18px, 2.2vw, 26px)"
            >
              <div style={{ marginBottom: 16 }}>
                <IconBadge tint={TINTS[i]}>{ICONS[i]}</IconBadge>
              </div>
              <h3 style={{
                fontSize: TYPE.cardTitle, fontWeight: WEIGHT.semibold,
                color: COLORS.textPrimary,
                marginBottom: 8, lineHeight: 1.3,
                letterSpacing: LETTER.tight,
              }}>
                {c.title}
              </h3>
              <p style={{
                fontSize: TYPE.cardText, color: 'rgba(255,255,255,0.58)',
                lineHeight: 1.6, fontWeight: WEIGHT.regular,
              }}>
                {c.text}
              </p>
            </GlassCard>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) { .trust-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 480px) { .trust-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}
